import { makeAutoObservable } from 'mobx'
import { decorate, inject, injectable } from 'inversify'

import { Api } from '@evertel/api'
import { SessionService } from './'
import { PushService } from '@evertel/push'
import { Store } from '@evertel/types'
import { MediaUploadService } from '@evertel/media'
import { LocalAsyncStorage } from '@evertel/utils'
import { FetchUserService } from '@evertel/blue-user'

export type LogoutReason = 'manual' | 'idle_timeout' | 'token_expired' | 'password_changed' | 'email_changed' | 'other'

const LOGOUT_REASON_STORAGE_KEY = 'logoutReason'

const INVOLUNTARY_LOGOUT_REASONS: string[] = ['idle_timeout', 'token_expired']
const CALLBACK_URL_FUTURE_MS = 20 * 60 * 1000 // 20 minutes into the future

class LogoutService {
    interceptorInitialized = false
    onLogoutCallback?: () => void = undefined
    private getCurrentUrl?: () => string | null = undefined

    constructor(
        private api: Api,
        private pushService: PushService,
        private sessionState: SessionService,
        private localAsyncStorage: LocalAsyncStorage,

        private mediaUploadService: MediaUploadService,
        private fetchUserService: FetchUserService
    ) {
        makeAutoObservable(this)
    }

    init() {
        this.initializeInterceptor()
    }

    initializeInterceptor() {
        if (this.interceptorInitialized) return

        this.api.addResponseInterceptor(
            null,
            async (error: any) => {
                if (
                    error.status !== 401
                    || !error.url
                    || error.url.includes('/testAccess')
                ) {
                    return Promise.reject(error)
                }

                if (this.sessionState.currentUserId) {
                    try {
                        await this.api.agent.get(`/BlueUsers/testAccess?_=${Date.now()}`)
                    } catch (error: any) {
                        if (error.status === 401) {
                            await this.logout('token_expired')
                        }
                    }
                }
                return Promise.reject(error)
            }
        )

        this.interceptorInitialized = true
    }

    // Set callback to notify when logout is called, in web it triggers a navigation to /login after logout() is called
    setOnLogoutCallback(onLogoutCb: () => void) {
        this.onLogoutCallback = onLogoutCb
    }

    // Platform-provided URL retriever. Called before destroy() on involuntary
    // logouts so the user returns to their current location after logging back in.
    setGetCurrentUrl(fn: () => string | null) {
        this.getCurrentUrl = fn
    }

    async logout(reason?: LogoutReason | string) {
        // Capture current URL before destroy clears session state.
        // Only for involuntary logouts (idle timeout, token expired) —
        // the user was using the app and should return here after re-login.
        if (this.getCurrentUrl && reason && INVOLUNTARY_LOGOUT_REASONS.includes(reason)) {
            const url = this.getCurrentUrl()
            if (url) {
                this.sessionState.setCallbackUrl(url, Date.now() + CALLBACK_URL_FUTURE_MS)
            }
        }

        try {
            // Store reason in localStorage before logout so login page can display it
            if (reason) {
                await this.localAsyncStorage.setItem(LOGOUT_REASON_STORAGE_KEY, reason)
            }

            // We only care that they're all attempted, it doesn't matter if one fails
            // there's a case with staging-api where cross subdomain cookies cause logout of other domains.
            await Promise.allSettled([
                this.pushService.deletePushToken(),
                this.api.authToken ? this.api.Routes.BlueUser.postLogout(reason) : Promise.resolve()
            ])

        } catch (e) {
            if (!this.api.isProduction) console.log('error', e)
        } finally {
            this.sessionState.destroy()

            // Clears every live Store instance (blue users, departments,
            // departments access, rooms, threads, messages, message drafts,
            // documents, etc.)
            Store.clearAll()

            // Non-Store state still needs explicit teardown
            this.mediaUploadService.clear()
            this.fetchUserService.destroy()

            if (this.onLogoutCallback) this.onLogoutCallback()
        }
    }

    async getStoredLogoutReason(): Promise<LogoutReason | null> {
        const reason = await this.localAsyncStorage.getItem(LOGOUT_REASON_STORAGE_KEY)
        return reason as LogoutReason | null
    }

    async clearStoredLogoutReason(): Promise<void> {
        await this.localAsyncStorage.removeItem(LOGOUT_REASON_STORAGE_KEY)
    }

    async logoutAllDevices(reason?: LogoutReason) {
        try {
            await this.api.Routes.BlueUser.postLogoutOtherDevices(this.sessionState.currentUserId)
        } finally {
            await this.logout(reason)
        }
    }
}

let i = 0
decorate(injectable(), LogoutService)
decorate(inject(Api), LogoutService, i++)
decorate(inject(PushService), LogoutService, i++)
decorate(inject(SessionService), LogoutService, i++)
decorate(inject(LocalAsyncStorage), LogoutService, i++)

decorate(inject(MediaUploadService), LogoutService, i++)
decorate(inject(FetchUserService), LogoutService, i++)

export { LogoutService }
